import { CdkDragEnd, Point } from '@angular/cdk/drag-drop';
import { Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { ChatService } from '../../services/chat.service';
import { ChatMessage, ChatMessageThread } from './models/chat-message.model';

@Component({
  selector: 'lib-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  standalone: false
})
export class ChatComponent implements OnDestroy {
  showChatWindow: boolean = false;
  isChatExpanded: boolean = false;
  userInput: string = '';
  messages: ChatMessage[] = [];
  isSending: boolean = false;
  exampleQuestions: string[] = [];
  launcherPosition: Point = { x: 0, y: 0 };

  @ViewChild('chatBody') private chatBody?: ElementRef<HTMLDivElement>;
  @ViewChild('dragBoundary') private dragBoundary?: ElementRef<HTMLDivElement>;

  private threadId: string | null = null;
  private chatSubscription?: Subscription;
  private exampleQuestionsRequested: boolean = false;
  private launcherSize: { width: number, height: number } = { width: 0, height: 0 };
  private launcherDragged: boolean = false;

  constructor(private chatService: ChatService) { }

  toggleChatWindow() {
    this.showChatWindow = !this.showChatWindow;

    if (this.showChatWindow) {
      this.loadExampleQuestions();
    }
  }

  openChatWindow(): void {
    if (this.launcherDragged) {
      return;
    }

    this.toggleChatWindow();
  }

  onLauncherPointerDown(): void {
    this.launcherDragged = false;
  }

  onLauncherDragStarted(): void {
    this.launcherDragged = true;
  }

  onLauncherDragEnded(event: CdkDragEnd): void {
    const launcher = event.source.element.nativeElement.getBoundingClientRect();

    this.launcherSize = { width: launcher.width, height: launcher.height };
    this.launcherPosition = event.source.getFreeDragPosition();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.clampLauncherPosition();
  }

  sendExampleQuestion(question: string): void {
    if (this.isSending) {
      return;
    }

    this.userInput = question;
    this.sendMessage();
  }

  toggleChatSize() {
    this.isChatExpanded = !this.isChatExpanded;
  }

  async sendMessage(): Promise<void> {
    const message = this.userInput.trim();

    if (!message || this.isSending) {
      return;
    }

    this.userInput = '';
    this.isSending = true;

    const messageThread = this.createMessageThread(this.threadId ?? '');

    this.messages.push({
      sender: 'user',
      thread: messageThread,
      text: message
    });

    const assistantMessage: ChatMessage = {
      sender: 'assistant',
      thread: messageThread,
      responseGroup: {
        request_id: messageThread.request_id,
        responses: []
      }
    };

    this.messages.push(assistantMessage);
    this.scrollToBottom();

    try {
      const threadId = await this.ensureThreadId();
      messageThread.thread_id = threadId;

      this.chatSubscription = this.chatService.sendMessage(message, threadId).subscribe({
        next: (response) => {
          assistantMessage.responseGroup.responses.push(response);
        },
        error: (error) => {
          console.error('Chat request failed', error);
          assistantMessage.responseGroup.responses.push({
            type: 'error',
            data: 'Unable to get an answer right now.'
          });
          this.isSending = false;
        },
        complete: () => {
          this.isSending = false;
        }
      });
    } catch {
      assistantMessage.responseGroup.responses.push({
        type: 'error',
        data: 'Unable to start the chat session.'
      });
      this.isSending = false;
    }
  }

  clearChat(): void {
    this.chatSubscription?.unsubscribe();
    this.messages = [];
    this.threadId = null;
    this.isSending = false;
  }

  ngOnDestroy(): void {
    this.chatSubscription?.unsubscribe();
  }

  private clampLauncherPosition(): void {
    const boundary = this.dragBoundary?.nativeElement.getBoundingClientRect();

    if (!boundary) {
      return;
    }

    const minX = Math.min(0, this.launcherSize.width - boundary.width);
    const minY = Math.min(0, this.launcherSize.height - boundary.height);

    this.launcherPosition = {
      x: Math.min(0, Math.max(minX, this.launcherPosition.x)),
      y: Math.min(0, Math.max(minY, this.launcherPosition.y))
    };
  }

  private async loadExampleQuestions(): Promise<void> {
    if (this.exampleQuestionsRequested) {
      return;
    }

    this.exampleQuestionsRequested = true;

    try {
      this.exampleQuestions = await this.chatService.getExampleQuestions();
    } catch (error) {
      console.error('Chat example questions request failed', error);
    }
  }

  private async ensureThreadId(): Promise<string> {
    if (!this.threadId) {
      const chatStart = await this.chatService.startChat();
      this.threadId = chatStart.thread_id;
    }

    return this.threadId;
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const element = this.chatBody?.nativeElement;
      if (element) {
        element.scrollTo({ top: element.scrollHeight, behavior: 'smooth' });
      }
    });
  }

  private createMessageThread(threadId: string): ChatMessageThread {
    return {
      thread_id: threadId,
      request_id: crypto.randomUUID()
    };
  }
}
